import {type ClassValue, clsx} from "clsx"
import {twMerge} from "tailwind-merge"
import {formatDate, parseISO} from "date-fns";
import {isDefined} from "@/helpers/sanitizer.ts";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDateString(date?: string) {
  if (!isDefined(date) || typeof date !== 'string') {
    return ""
  }
  try {
    // parseISO will handle UTC timestamps (ending with 'Z') correctly
    // and convert them to the local timezone
    const dateStr = parseISO(date)
    return formatDate(dateStr, "dd.MM.yyyy")
  } catch {
    return ""
  }
}

export function formatTimeString(date?: string) {
  if (!isDefined(date)) {
    return ""
  }

  // parseISO will handle UTC timestamps (ending with 'Z') correctly
  // and convert them to the local timezone
  const timeStr = parseISO(date!)
  return formatDate(timeStr, "HH:mm")
}

export function formatDateTimeString(date?: string) {
  if (!isDefined(date) || typeof date !== 'string') {
    return ""
  }
  try {
    // parseISO will handle UTC timestamps (ending with 'Z') correctly
    // and convert them to the local timezone
    const dateTimeStr = parseISO(date)
    return formatDate(dateTimeStr, "dd.MM.yyyy HH:mm")
  } catch {
    return ""
  }
}

export function formatDurationToHHMM(startTime: string, endTime: string): string {
  // Parse times in HH:mm format
  const [startHours, startMinutes] = startTime.split(':').map(Number)
  const [endHours, endMinutes] = endTime.split(':').map(Number)
  
  // Calculate total minutes
  const startTotalMinutes = startHours * 60 + startMinutes
  const endTotalMinutes = endHours * 60 + endMinutes
  let durationMinutes = endTotalMinutes - startTotalMinutes
  
  // Handle negative duration (end time before start time)
  if (durationMinutes < 0) {
    durationMinutes += 24 * 60 // Add 24 hours
  }
  
  // Convert to hours and minutes
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60
  
  // Format as hH:MM (h optional for 2+ digits, H always shown, MM always 2 digits)
  if (hours >= 10) {
    return `${hours}:${minutes.toString().padStart(2, '0')}`
  } else {
    return `${hours}:${minutes.toString().padStart(2, '0')}`
  }
}

export function formatCurrency(amount: number | string): string {
  // Convert to number if string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  
  // Format for German locale with 2 decimal places
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numAmount) + ' €'
}

export function parseCurrencyInput(input: string): number {
  // Remove currency symbol and whitespace
  const cleaned = input.replace(/[€\s]/g, '')
  // Support both comma and dot as decimal separator
  const normalized = cleaned.replace(',', '.')
  return parseFloat(normalized) || 0
}

export function isValidCurrencyInput(input: string): boolean {
  // Accept both German (comma) and English (dot) formats
  // Examples: "0", "15", "123,45", "1234.56", "123.45", "1234,56"
  return /^\d{1,4}([.,]\d{1,2})?$/.test(input.trim())
}
